import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';

import useVideoRecorder, { STATUS } from './useVideoRecorder';

const formatSeconds = (total) => {
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// iOS WebKit intermittently composites CSS-transformed camera <video> as solid black (the
// stream itself is healthy — frames arrive and play). Skip the cosmetic mirror there; everyone
// else gets the Zoom-style mirrored self-view.
const IS_IOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
const MIRROR_TRANSFORM = IS_IOS ? 'none' : 'scaleX(-1)';

const STAGE_STYLE = {
 background: '#000', borderRadius: '.5rem', overflow: 'hidden', aspectRatio: '16 / 9',
};

// In-browser video recorder. Owns no upload concerns — it hands the recorded blob back via onRecorded
// and lets the composer do the Cloudinary upload. See useVideoRecorder for the state machine.
function VideoRecorderModal({
 show, onHide, onRecorded, maxSeconds = 120, sidePanel = null, modalClassName = null,
}) {
  const {
    status,
    countdownValue,
    resumeCountdownValue,
    elapsedSeconds,
    recordedUrl,
    livePreviewRef,
    cameras,
    mics,
    cameraId,
    micId,
    cameraOn,
    micOn,
    paused,
    selectCamera,
    selectMic,
    toggleCamera,
    toggleMic,
    beginCountdown,
    togglePause,
    restart,
    stopRecording,
    reRecord,
    confirm,
    retry,
  } = useVideoRecorder({
 show, maxSeconds, onRecorded, onHide,
});

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  // Set when the close-guard auto-paused an active recording, so "Keep editing" knows to resume it
  // (and we don't resume a recording the user had paused themselves).
  const pausedForCloseRef = useRef(false);

  const showLiveStage = status === STATUS.READY || status === STATUS.COUNTDOWN || status === STATUS.RECORDING;

  // Closing mid-recording or with an unconfirmed take would throw the recording away — confirm first.
  const hasUnsavedTake = status === STATUS.RECORDING || status === STATUS.PREVIEW;
  const handleRequestClose = () => {
    if (!hasUnsavedTake) {
      onHide();
      return;
    }
    // Freeze an actively-recording take while the user decides, so it doesn't roll on behind the
    // modal. A running resume countdown is cancelled the same way (togglePause cancels it) so the
    // recording can't restart behind the dialog; "Keep editing" re-runs the countdown.
    if (status === STATUS.RECORDING && (!paused || resumeCountdownValue != null)) {
      togglePause();
      pausedForCloseRef.current = true;
    }
    setShowConfirmClose(true);
  };
  const keepEditing = () => {
    if (pausedForCloseRef.current) {
      togglePause();
      pausedForCloseRef.current = false;
    }
    setShowConfirmClose(false);
  };
  const discardAndClose = () => {
    pausedForCloseRef.current = false;
    setShowConfirmClose(false);
    onHide();
  };

  return (
    <>
      <Modal show={show} onHide={handleRequestClose} centered size={sidePanel ? 'xl' : 'lg'} className={modalClassName}>
        <Modal.Header closeButton className="border-0">
          <Modal.Title style={{ fontSize: '18px' }}>
            Record video up to {Math.round(maxSeconds / 60)} minute{maxSeconds === 60 ? '' : 's'}
          </Modal.Title>
        </Modal.Header>

        {/* With a sidePanel (video pitches: the opportunity text), the body splits into recorder +
            a scrollable reading pane so the source can answer the questions while recording. */}
        <Modal.Body className={sidePanel ? 'd-flex gap-4 align-items-start' : undefined}>
          {sidePanel && (
            <div
              className="recorder-side-panel order-2 flex-shrink-0 border-start ps-4"
              style={{ width: '38%', maxHeight: '60vh', overflowY: 'auto' }}
              data-testid="recorder-side-panel"
            >
              {sidePanel}
            </div>
          )}
          <div className={sidePanel ? 'order-1 flex-grow-1' : undefined} style={sidePanel ? { minWidth: 0 } : undefined}>
          {status === STATUS.REQUESTING && (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5">
              <div className="spinner-border mb-3" role="status" aria-hidden="true" />
              <div>Requesting camera and microphone access…</div>
            </div>
          )}

          {status === STATUS.DENIED && (
            <div className="text-center py-4">
              <i className="fa-solid fa-video-slash fa-2x text-secondary mb-3" aria-hidden="true" />
              <p className="mb-1 fw-semibold">Camera and microphone access is blocked</p>
              <p className="text-muted small mb-0">
                Allow access in your browser, then try again. You can also close this and upload a video file instead.
              </p>
            </div>
          )}

          {status === STATUS.UNSUPPORTED && (
            <div className="text-center py-4">
              <i className="fa-solid fa-circle-exclamation fa-2x text-secondary mb-3" aria-hidden="true" />
              <p className="mb-1 fw-semibold">Your browser can&apos;t record video here</p>
              <p className="text-muted small mb-0">Close this and upload a video file instead.</p>
            </div>
          )}

          {showLiveStage && (
            <div className="position-relative" style={STAGE_STYLE}>
              {/* scaleX(-1) mirrors the self-view like Zoom/Meet — your movements match what you
                  expect from a mirror. Only the live preview is flipped; the recording itself
                  (and its playback below) stay un-mirrored, i.e. how recipients see you. */}
              <video
                ref={livePreviewRef}
                muted
                autoPlay
                playsInline
                className="w-100 h-100"
                style={{ objectFit: 'cover', display: 'block', transform: MIRROR_TRANSFORM }}
                data-testid="live-preview"
              >
                <track kind="captions" />
              </video>

              {(status === STATUS.COUNTDOWN || resumeCountdownValue != null) && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  data-testid="countdown-overlay"
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 160, height: 160, background: 'rgba(0,0,0,.55)' }}
                  >
                    <span className="text-white fw-bold" style={{ fontSize: '4.5rem', lineHeight: 1 }}>
                      {resumeCountdownValue ?? countdownValue}
                    </span>
                  </div>
                </div>
              )}

              {status === STATUS.READY && !cameraOn && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white-50">
                  <i className="fa-solid fa-video-slash fa-2x mb-2" aria-hidden="true" />
                  <span className="small">Camera off</span>
                </div>
              )}

              {status === STATUS.READY && (
                <div className="position-absolute bottom-0 start-0 m-2 d-flex gap-2">
                  <button
                    type="button"
                    className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${
                      micOn ? 'btn-light' : 'btn-danger'
                    }`}
                    style={{ width: 40, height: 40 }}
                    title={micOn ? 'Mute microphone' : 'Unmute microphone'}
                    aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'}
                    aria-pressed={!micOn}
                    onClick={toggleMic}
                  >
                    <i className={`fa-solid ${micOn ? 'fa-microphone' : 'fa-microphone-slash'}`} />
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${
                      cameraOn ? 'btn-light' : 'btn-danger'
                    }`}
                    style={{ width: 40, height: 40 }}
                    title={cameraOn ? 'Turn camera off' : 'Turn camera on'}
                    aria-label={cameraOn ? 'Turn camera off' : 'Turn camera on'}
                    aria-pressed={!cameraOn}
                    onClick={toggleCamera}
                  >
                    <i className={`fa-solid ${cameraOn ? 'fa-video' : 'fa-video-slash'}`} />
                  </button>
                </div>
              )}
            </div>
          )}

          {status === STATUS.READY && (
            <div className="d-flex flex-column flex-sm-row gap-2 mt-3">
              <div className="input-group flex-fill">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa-solid fa-video text-neutral-100" aria-hidden="true" />
                </span>
                <select
                  className="form-select border-start-0"
                  aria-label="Camera"
                  value={cameraId || ''}
                  onChange={(e) => selectCamera(e.target.value)}
                >
                  {cameras.length === 0 && <option>Default camera</option>}
                  {cameras.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-group flex-fill">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fa-solid fa-microphone text-neutral-100" aria-hidden="true" />
                </span>
                <select
                  className="form-select border-start-0"
                  aria-label="Microphone"
                  value={micId || ''}
                  onChange={(e) => selectMic(e.target.value)}
                >
                  {mics.length === 0 && <option>Default microphone</option>}
                  {mics.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Microphone ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {status === STATUS.PREVIEW && IS_IOS && (
            <div
              style={STAGE_STYLE}
              ref={(host) => {
                if (!host || host.dataset.mounted) return;
                // eslint-disable-next-line no-param-reassign -- marking the host node is the point
                host.dataset.mounted = '1';
                // Imperative player: React-reconciled <video> wedges iOS WebKit's media loader
                // (plays fine as a bare element) — same fix as the thread player.
                const el = document.createElement('video');
                el.src = recordedUrl;
                el.controls = true;
                el.setAttribute('playsinline', '');
                el.style.cssText = 'width:100%;height:100%;object-fit:contain;';
                host.appendChild(el);
              }}
            />
          )}
          {status === STATUS.PREVIEW && !IS_IOS && (
            <video
              src={recordedUrl}
              controls
              playsInline
              className="w-100 rounded border"
              style={{ ...STAGE_STYLE, objectFit: 'contain' }}
            >
              <track kind="captions" />
            </video>
          )}
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0">
          {status === STATUS.DENIED && (
            <>
              <Button variant="secondary" onClick={handleRequestClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={retry}>
                Try again
              </Button>
            </>
          )}

          {status === STATUS.UNSUPPORTED && (
            <Button variant="secondary" onClick={handleRequestClose}>
              Close
            </Button>
          )}

          {status === STATUS.READY && (
            <>
              <Button variant="secondary" onClick={handleRequestClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={beginCountdown}>
                Begin recording
              </Button>
            </>
          )}

          {(status === STATUS.COUNTDOWN || status === STATUS.RECORDING) && (
            <div className="d-flex align-items-center justify-content-between w-100">
              <span
                className={`d-flex align-items-center gap-2 ${
                  elapsedSeconds >= maxSeconds - 10 ? 'text-danger' : 'text-secondary'
                }`}
                data-testid="elapsed"
              >
                <i className="fa-regular fa-clock" aria-hidden="true" />
                {formatSeconds(elapsedSeconds)}/{formatSeconds(maxSeconds)}
              </span>
              <div className="d-flex align-items-center gap-2">
                <Button variant="outline-secondary" onClick={restart} title="Restart" aria-label="Restart recording">
                  <i className="fa-solid fa-rotate-left" />
                </Button>
                {/* During the resume countdown the button reads Pause again — clicking cancels the countdown. */}
                <Button
                  variant="outline-secondary"
                  onClick={togglePause}
                  disabled={status === STATUS.COUNTDOWN}
                  title={paused && resumeCountdownValue == null ? 'Resume' : 'Pause'}
                  aria-label={paused && resumeCountdownValue == null ? 'Resume recording' : 'Pause recording'}
                >
                  <i className={`fa-solid ${paused && resumeCountdownValue == null ? 'fa-play' : 'fa-pause'}`} />
                </Button>
                <Button variant="danger" onClick={stopRecording} disabled={status === STATUS.COUNTDOWN}>
                  <i className="fa-solid fa-stop me-1" /> Stop Recording
                </Button>
              </div>
            </div>
          )}

          {status === STATUS.PREVIEW && (
            <>
              <Button variant="secondary" onClick={reRecord}>
                <i className="fa-solid fa-rotate-left me-1" /> Re-record
              </Button>
              <Button variant="primary" onClick={confirm}>
                <i className="fa-solid fa-check me-1" /> Use this video
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Overlays the recorder; react-bootstrap stacks it above with its own backdrop. */}
      <Modal show={showConfirmClose} onHide={keepEditing} centered size="sm" animation={false} className={modalClassName}>
        <Modal.Header closeButton className="border-0">
          <Modal.Title style={{ fontSize: '18px' }}>Discard recording?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-secondary">
          You haven&apos;t added this video to your message yet. If you close now, the recording will be lost.
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={keepEditing}>
            Keep editing
          </Button>
          <Button variant="danger" onClick={discardAndClose}>
            Discard
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

VideoRecorderModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onRecorded: PropTypes.func.isRequired,
  maxSeconds: PropTypes.number,
  sidePanel: PropTypes.node,
  modalClassName: PropTypes.string,
};

export default VideoRecorderModal;
